import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTree,
  MatTreeNodeDef,
  MatTreeNode,
  MatTreeNodePadding,
  MatTreeNodeToggle,
} from '@angular/material/tree';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { filter, finalize, Subscription, switchMap, tap } from 'rxjs';
import { FlatNode } from 'src/app/models/gum-models/flat-node';
import { Organization } from 'src/app/models/organization.model';
import { GumOrganizationsService } from 'src/app/services/gumservices/gum-organizations.service';
import { GumUsersService } from 'src/app/services/gumservices/gum-users.service';
import { DialogDeleteConfirmationComponent } from '../dialogs/dialog-delete/dialog-delete-confirmation.component';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatSuffix } from '@angular/material/form-field';

import { RouterLink } from '@angular/router';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import {
  MatButtonToggleGroup,
  MatButtonToggle,
} from '@angular/material/button-toggle';

@Component({
  selector: 'app-gum-organizations',
  templateUrl: './gum-organizations.component.html',
  styleUrl: './gum-organizations.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButtonToggleGroup,
    MatButtonToggle,
    MatTooltip,
    MatIcon,
    MatIconButton,
    RouterLink,
    ReactiveFormsModule,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatFormField,
    MatInput,
    MatSuffix,
    MatCellDef,
    MatCell,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    MatTree,
    MatTreeNodeDef,
    MatTreeNode,
    MatTreeNodePadding,
    MatButton,
    MatTreeNodeToggle,
    NgxSpinnerModule,
  ],
})
export class GumOrganizationsComponent implements OnInit, OnDestroy {
  private spinnerService = inject(NgxSpinnerService);
  displayedColumns: string[] = [
    'id',
    'display',
    'displayParentOrganization',
    'billing',
    'createdAt',
    'actions',
  ];
  dataSource!: MatTableDataSource<Organization>;
  deleteConfirmationDialog: MatDialogRef<
    DialogDeleteConfirmationComponent,
    any
  > | null = null;
  filtersForm: UntypedFormGroup;

  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort!: MatSort;

  organizationChange!: Subscription;

  /** Tree */
  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );
  treeFlattener = new MatTreeFlattener(
    this.transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );
  treeDataSource = new MatTreeFlatDataSource(
    this.treeControl,
    this.treeFlattener
  );

  /**
   * Constructeur du composant
   * @param organizationsService Instance du service des organisations
   * @param usersService Instance du service des utilisateurs
   * @param dialog Instance du service MatDialog pour les fenêtres
   * @param translate Instance du service de traduction
   * @param toastr Instance du service toastr pour les popups
   * @param exportExcelService Instance du service d'export Excel
   * @param importExcelService Instance du service d'import Excel
   * @param formbuilder Instance du service Formbuilder pour les formaulaires
   */
  constructor(
    private organizationsService: GumOrganizationsService,
    private usersService: GumUsersService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private formbuilder: UntypedFormBuilder
  ) {
    this.filtersForm = new UntypedFormGroup({});
  }

  /**
   * A l'initialisation du composant
   */
  ngOnInit() {
    this.initFiltersForm();

    this.organizationChange =
      this.organizationsService.currentOrganizationChange$.subscribe(
        (organization) => {
          if (organization) {
            this.loadDatas();
          }
        }
      );

    this.loadDatas();
  }
  /**
   * A la fin de vie du composant
   */
  ngOnDestroy() {
    this.organizationChange.unsubscribe();
  }

  private transformer(node: any, level: number) {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.display,
      id: node.id,
      level: level,
    };
  }

  private isPartnerUid(uid?: string): boolean {
    return !!uid && /^\.\d+\.\d+$/.test(uid);
  }

  hasChild(_: number, node: FlatNode) {
    return node.expandable;
  }

  createTree(organizations: Organization[]): any[] {
    const racine = organizations.find(
      (a) => !organizations.find((b) => a.organizationId === b.id)
    );

    const nest = (
      items: Organization[],
      id: string = racine ? racine.organizationId : '',
      link: keyof Organization = 'organizationId'
    ): any[] =>
      items
        .filter((item) => item[link] === id)
        .map((item) => ({ ...item, children: nest(items, item.id) }));

    return nest(
      organizations.sort((a, b) =>
        a.display > b.display ? 1 : b.display > a.display ? -1 : 0
      )
    );
  }
  /**
   * Initialisation du formulaire des filtres du tableau des organisations
   */
  initFiltersForm() {
    this.filtersForm = this.formbuilder.group({
      id: [''],
      display: [''],
      billing: [''],
      displayParentOrganization: [''],
      createdAt: [''],
    });
  }

  /**
   * Chargement des données
   */
  loadDatas() {
    this.spinnerService.show('spinner');

    this.organizationsService.getOrganisations().subscribe((organizations) => {
      // Filtre PARTENAIRES selon UID format .X.Y
      const partenaires = organizations.filter((org) =>
        this.isPartnerUid(org.billing)
      );

      this.treeDataSource.data = this.createTree(partenaires);
      this.dataSource = new MatTableDataSource(partenaires);

      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;

      this.dataSource.filterPredicate = (
        data: Organization,
        filter: string
      ) => {
        const filterArray = filter.split('¤');
        const matchFilter = [];

        matchFilter.push(
          data.id &&
            data.id
              .trim()
              .toLowerCase()
              .includes(filterArray[0].trim().toLowerCase())
        );
        matchFilter.push(
          data.display &&
            data.display
              .trim()
              .toLowerCase()
              .includes(filterArray[1].trim().toLowerCase())
        );
        matchFilter.push(
          data.displayParentOrganization
            .trim()
            .toLowerCase()
            .includes(filterArray[2].trim().toLowerCase())
        );
        matchFilter.push(
          data.billing &&
            data.billing
              .trim()
              .toLowerCase()
              .includes(filterArray[3].trim().toLowerCase())
        );

        if (filterArray[4] !== '') {
          matchFilter.push(
            !!data.createdAt &&
              new Date(data.createdAt)
                .toLocaleDateString()
                .includes(new Date(filterArray[4]).toLocaleDateString())
          );
        }

        return matchFilter.every(Boolean);
      };

      if (this.usersService.isDevAccount()) {
        this.displayedColumns = [
          'id',
          'display',
          'displayParentOrganization',
          'billing',
          'createdAt',
          'actions',
        ];
      } else {
        this.displayedColumns = [
          'display',
          'displayParentOrganization',
          'billing',
          'createdAt',
          'actions',
        ];
      }

      this.applyFilters();

      this.spinnerService.hide('spinner');
    });
  }

  /**
   * Application du filtre dans le tableau des organisations
   */
  applyFilters() {
    const id = this.filtersForm.get('id')?.value;
    const display = this.filtersForm.get('display')?.value;
    const displayParentOrganization = this.filtersForm.get(
      'displayParentOrganization'
    )?.value;
    const billing = this.filtersForm.get('billing')?.value;
    const createdAt = this.filtersForm.get('createdAt')?.value;

    const filterValue =
      (!!id ? id : '') +
      '¤' +
      (!!display ? display : '') +
      '¤' +
      (!!displayParentOrganization ? displayParentOrganization : '') +
      '¤' +
      (!!billing ? billing : '') +
      '¤' +
      (!!createdAt && createdAt !== '' ? createdAt : '');

    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
   * Click sur le bouton pour supprimer une organisation
   * @param organizationName Nom de l'organisation
   * @param organizationId Identifiant de l'organisation
   */
  onDeleteClick(organizationName: string, organizationId: string) {
    this.deleteConfirmationDialog = this.dialog.open(
      DialogDeleteConfirmationComponent,
      { panelClass: 'default-dialog', disableClose: true }
    );

    // Message de confirmation en dur
    this.deleteConfirmationDialog.componentInstance.confirmMessage = `Confirmer la suppression de l'organisation : ${organizationName}`;

    this.deleteConfirmationDialog
      .afterClosed()
      .pipe(
        filter((result) => result === true),
        tap(() => this.spinnerService.show('spinner')),
        switchMap(() =>
          this.organizationsService.deleteOrganization(organizationId)
        ),
        finalize(() => {
          this.spinnerService.hide('spinner');
          this.deleteConfirmationDialog = null;
        })
      )
      .subscribe({
        next: () => {
          this.toastr.success(
            `Organisation "${organizationName}" supprimée avec succès.`
          );
          this.loadDatas();
        },
      });
  }

  /**
   * Réinitialise le filtre de la colonne spécifiée.
   * @param formControlName Nom du contrôle du formulaire
   */
  resetColumnFilter(formControlName: string): void {
    this.filtersForm.controls[formControlName].reset();
    this.applyFilters();
  }

  /**
   * Réinitialise tous les filtres de colonne
   */
  resetAllColumnFilter() {
    this.filtersForm.reset();
    this.applyFilters();
  }
}
